// A-06 — Registre des dossiers : segments, filtres pastilles, table enrichie.
import { useEffect, useMemo, useState } from 'react';
import {
  useDossierRegistrySettings,
  useEffectiveDossierFields,
} from '../../hooks/queries/useCabinetSettings';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DOSSIER_LIST_JURISDICTION_PARAM,
  DOSSIER_LIST_SCOPE_ALL,
  DOSSIER_LIST_SCOPE_PARAM,
  DOSSIER_LIST_TYPE_PARAM,
  readListParam,
  writeListParam,
} from '../../lib/dossiers/dossierListLinks';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Archive, FolderOpen, Plus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { Avatar } from '../ui/Avatar';
import { cn } from '../../lib/cn';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { useAuthStore } from '../../store/useAuthStore';
import { asCollaboratorRole, canAccessFeature } from '../../services/auth/permissions';
import { useDossiersEnriched } from '../../hooks/queries/useDossiers';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import {
  DOSSIER_STAGE_LABELS,
  DOSSIER_TYPE_LABELS,
  type DossierType,
} from '../../services/dossiers/dossierService';
import { loadParcoursCatalog } from '../../services/dossiers/friseService';
import {
  boardFrises,
  currentStepLabel,
  dossierBoardRef,
  stepFilterChoices,
  type BoardFrise,
} from '../../lib/dossiers/friseBoard';
import { friseChoiceLabel, type OfferedFrise } from '../../lib/dossiers/parcoursDefaults';
import {
  type DossierListItem,
  type EcheanceFilter,
  type RegistreSortKey,
  type SortDir,
  compareRegistre,
  deadlineUrgency,
  lastActivity,
  matchesEcheanceFilter,
  matchesSearch,
  nextDeadline,
  primaryPartyName,
  registreSortIsDate,
} from '../../services/dossiers/dossierListDerive';
import { DossierCreateModal } from './DossierCreateModal';
import { DossierViewSelect } from './DossierViewSelect';
import { PersonSelect } from '../ui/PersonSelect';
import {
  ListEmpty,
  ListFooter,
  ListToolbar,
  Segments,
  ToolbarFilters,
  ToolbarGrow,
  ToolbarSearch,
} from '../ui/ListToolbar';

import { ResizableColumns } from '../ui/ResizableColumns';
import { FilterPill, MENU_PANEL, MenuChevron, MenuMark } from '../ui/FilterPill';
const TYPES: DossierType[] = [
  'civil', 'penal', 'commercial', 'social',
  'immobilier', 'famille', 'admin', 'autre',
];

type Segment = 'mine' | 'all' | 'closed';
const SEGMENT_KEY = 'cime.dossiers.segment';
const COLUMNS_KEY = 'cime.dossiers.columns';

const OPTIONAL_COLUMNS = [
  { key: 'adv', label: 'Adversaire', group: 'registre', on: true },
  { key: 'activite', label: 'Activité', group: 'registre', on: true },
  { key: 'audience', label: 'Audience', group: 'extra', on: false },
  { key: 'prescription', label: 'Prescription', group: 'extra', on: false },
  { key: 'matiere', label: 'Type de dossier', group: 'extra', on: false },
  { key: 'juridiction', label: 'Juridiction', group: 'extra', on: false },
  { key: 'rg', label: 'N° RG', group: 'extra', on: false },
  { key: 'ouverture', label: 'Ouverture', group: 'extra', on: false },
  { key: 'aj', label: 'AJ', group: 'extra', on: false },
] as const;

type OptionalColumn = (typeof OPTIONAL_COLUMNS)[number]['key'];
type ColumnFlags = Record<OptionalColumn, boolean>;

const ECHEANCE_OPTIONS: { value: EcheanceFilter; label: string }[] = [
  { value: 'overdue', label: 'Dépassées' },
  { value: 'week', label: 'Dans les 7 jours' },
  { value: 'none', label: 'Sans date' },
];

const SORT_LABELS: Record<RegistreSortKey, string> = {
  affaire: 'Affaire',
  client: 'Client',
  adv: 'Adversaire',
  equipe: 'Équipe',
  etape: 'Étape',
  echeance: 'Échéance',
  audience: 'Audience',
  prescription: 'Prescription',
  matiere: 'Type de dossier',
  juridiction: 'Juridiction',
  rg: 'N° RG',
  ouverture: 'Ouverture',
  aj: 'AJ',
  activite: 'Activité',
};

const URGENCY_TEXT: Record<string, string> = {
  overdue: 'text-[#c05b33]',
  urgent: 'text-[#c05b33]',
  soon: 'text-cime-gold',
  normal: 'text-cime-text-primary',
};

function defaultColumns(): ColumnFlags {
  return Object.fromEntries(OPTIONAL_COLUMNS.map((col) => [col.key, col.on])) as ColumnFlags;
}

function loadColumns(): ColumnFlags {
  const flags = defaultColumns();
  try {
    const raw = localStorage.getItem(COLUMNS_KEY);
    if (!raw) return flags;
    const parsed = JSON.parse(raw) as Partial<ColumnFlags>;
    for (const col of OPTIONAL_COLUMNS) {
      const value = parsed[col.key];
      if (typeof value === 'boolean') flags[col.key] = value;
    }
  } catch {
    return defaultColumns();
  }
  return flags;
}

function friseLabel(frise: BoardFrise): string {
  return friseChoiceLabel(
    {
      familySlug: frise.familySlug,
      familyName: frise.familyName,
      frise: { name: frise.name } as OfferedFrise['frise'],
    },
    undefined,
  );
}

function formatWhen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'EEE d MMM', { locale: fr });
}

function sortCaption(key: RegistreSortKey, dir: SortDir): string {
  const how = registreSortIsDate(key)
    ? (dir === 'asc' ? 'la plus proche' : 'la plus lointaine')
    : (dir === 'asc' ? 'croissant' : 'décroissant');
  return `tri ${SORT_LABELS[key].toLocaleLowerCase('fr')}, ${how}`;
}

export function DossiersListPage() {
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const canCreate = canAccessFeature(asCollaboratorRole(collaborator?.role), 'dossiers_create');

  const [segment, setSegment] = useState<Segment>(() => {
    const saved = localStorage.getItem(SEGMENT_KEY);
    return saved === 'all' || saved === 'closed' ? saved : 'mine';
  });
  const [search, setSearch] = useState('');
  const [friseId, setFriseId] = useState<string | null>(null);
  const [stepFilter, setStepFilter] = useState<string[]>([]);
  // Type d'affaire et juridiction vivent dans l'adresse : la liste filtrée se
  // partage par lien (en-tête d'un dossier, réglages) et survit au rechargement.
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = useMemo(
    () => readListParam(searchParams, DOSSIER_LIST_TYPE_PARAM).filter((t): t is DossierType => TYPES.includes(t as DossierType)),
    [searchParams],
  );
  const juridictionFilter = useMemo(
    () => readListParam(searchParams, DOSSIER_LIST_JURISDICTION_PARAM),
    [searchParams],
  );
  const setTypeFilter = (values: DossierType[]) =>
    setSearchParams((sp) => writeListParam(sp, DOSSIER_LIST_TYPE_PARAM, values), { replace: true });
  const setJuridictionFilter = (values: string[]) =>
    setSearchParams((sp) => writeListParam(sp, DOSSIER_LIST_JURISDICTION_PARAM, values), { replace: true });
  const [leadFilter, setLeadFilter] = useState<string[]>([]);
  // Arrivée par un lien « tous les dossiers de ce type / de cette juridiction » :
  // on montre tout le cabinet, sans changer le choix mémorisé de la personne.
  const scopeAll = searchParams.get(DOSSIER_LIST_SCOPE_PARAM) === DOSSIER_LIST_SCOPE_ALL;
  useEffect(() => {
    if (!scopeAll) return;
    setSegment('all');
    setSearchParams((sp) => {
      const next = new URLSearchParams(sp);
      next.delete(DOSSIER_LIST_SCOPE_PARAM);
      return next;
    }, { replace: true });
  }, [scopeAll, setSearchParams]);
  const [echeanceFilter, setEcheanceFilter] = useState<EcheanceFilter | null>(null);
  const [sortKey, setSortKey] = useState<RegistreSortKey>('echeance');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [storedColumns, setColumns] = useState<ColumnFlags>(loadColumns);
  const { data: registry } = useDossierRegistrySettings();
  const shownFilters = registry?.filters ?? [];
  const dossierFields = useEffectiveDossierFields();
  // Prescription et AJ suivent le réglage du cabinet, quel que soit le choix mémorisé.
  const hiddenColumns = useMemo<OptionalColumn[]>(
    () => [
      ...(dossierFields.prescription ? [] : (['prescription'] as const)),
      ...(dossierFields.legalAid ? [] : (['aj'] as const)),
    ],
    [dossierFields.prescription, dossierFields.legalAid],
  );
  const columns = useMemo<ColumnFlags>(() => {
    const next = { ...storedColumns };
    for (const key of hiddenColumns) next[key] = false;
    return next;
  }, [storedColumns, hiddenColumns]);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: dossiers, isLoading } = useDossiersEnriched({
    type_affaire: typeFilter.length ? typeFilter : undefined,
    include_archived: segment === 'closed',
    include_closed: segment === 'closed',
  });
  const { data: catalog } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-parcours-catalog'],
    queryFn: loadParcoursCatalog,
  });
  const frises = useMemo(() => boardFrises(catalog ?? []), [catalog]);
  const stepChoices = stepFilterChoices(frises, friseId);
  const { data: collaborators } = useCollaborators();

  function selectSegment(next: Segment) {
    setSegment(next);
    localStorage.setItem(SEGMENT_KEY, next);
  }

  function selectSort(key: RegistreSortKey) {
    if (sortKey === key) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  function saveColumns(next: ColumnFlags) {
    setColumns(next);
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(next));
  }

  const collaboratorName = (id: string | null | undefined) =>
    collaborators?.find((c) => c.id === id)?.full_name ?? null;
  const collaboratorAvatar = (id: string | null | undefined) =>
    collaborators?.find((c) => c.id === id)?.avatar_url ?? null;

  const visible = useMemo(() => {
    let items = dossiers ?? [];
    if (segment === 'mine' && collaborator?.id) {
      items = items.filter((d) =>
        d.dossier_members.some((m) => m.collaborator_id === collaborator.id),
      );
    }
    if (segment === 'closed') {
      items = items.filter((d) => d.closed_at || d.archived_at);
    }
    if (friseId) {
      items = items.filter((d) => dossierBoardRef(d).friseTemplateId === friseId);
      if (stepFilter.length) {
        items = items.filter((d) => stepFilter.includes(String(dossierBoardRef(d).position)));
      }
    }
    if (leadFilter.length) {
      items = items.filter((d) => d.lead_counsel_id && leadFilter.includes(d.lead_counsel_id));
    }
    if (juridictionFilter.length) {
      items = items.filter((d) => d.juridiction && juridictionFilter.includes(d.juridiction));
    }
    if (echeanceFilter) {
      items = items.filter((d) => matchesEcheanceFilter(d, echeanceFilter));
    }
    if (search.trim()) {
      items = items.filter((d) => matchesSearch(d, search));
    }
    const ctx = {
      leadName: (id: string | null) => collaborators?.find((c) => c.id === id)?.full_name ?? null,
      stepLabel: (item: DossierListItem) =>
        currentStepLabel(item.dossier_frise_steps, dossierBoardRef(item).position)
        ?? DOSSIER_STAGE_LABELS[item.stage],
    };
    return [...items].sort((a, b) => compareRegistre(a, b, sortKey, sortDir, ctx));
  }, [
    dossiers, segment, collaborator?.id, friseId, stepFilter, leadFilter,
    juridictionFilter, echeanceFilter, search, sortKey, sortDir, collaborators,
  ]);

  const mineCount = useMemo(
    () =>
      (dossiers ?? []).filter(
        (d) =>
          !d.closed_at && !d.archived_at &&
          d.dossier_members.some((m) => m.collaborator_id === collaborator?.id),
      ).length,
    [dossiers, collaborator?.id],
  );
  const allCount = useMemo(
    () => (dossiers ?? []).filter((d) => !d.closed_at && !d.archived_at).length,
    [dossiers],
  );

  const juridictionOptions = useMemo(() => {
    // Une juridiction filtrée reste proposée même si aucun dossier affiché ne la porte.
    const set = new Set<string>(juridictionFilter);
    for (const d of dossiers ?? []) if (d.juridiction) set.add(d.juridiction);
    return Array.from(set).sort().map((j) => ({ value: j, label: j }));
  }, [dossiers, juridictionFilter]);

  const hasActiveFilters =
    friseId !== null || stepFilter.length > 0 || typeFilter.length > 0 || leadFilter.length > 0 ||
    juridictionFilter.length > 0 || echeanceFilter !== null;

  function resetFilters() {
    setFriseId(null);
    setStepFilter([]);
    setLeadFilter([]);
    setEcheanceFilter(null);
    setSearchParams(
      (sp) => writeListParam(writeListParam(sp, DOSSIER_LIST_TYPE_PARAM, []), DOSSIER_LIST_JURISDICTION_PARAM, []),
      { replace: true },
    );
  }

  function captionFor(item: DossierListItem): string | null {
    const id = dossierBoardRef(item).friseTemplateId;
    const frise = frises.find((entry) => entry.id === id);
    return frise ? friseLabel(frise) : null;
  }

  return (
    <>
      <div className="main-container-gdm flex min-w-0 flex-col gap-3.5">
        <div className="flex flex-col gap-2">
          <ListToolbar>
            <Segments
              label="Périmètre des dossiers"
              value={segment}
              onChange={selectSegment}
              options={[
                { value: 'all', label: 'Tous les dossiers', count: allCount },
                { value: 'mine', label: 'Mes dossiers', count: mineCount },
                { value: 'closed', label: 'Archivé' },
              ]}
            />
            <ToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Rechercher — n°, intitulé, objet, client, RG…"
              ariaLabel="Rechercher un dossier"
              className="w-[360px]"
            />
            <ToolbarGrow />
            <DossierViewSelect />
            <ColumnMenu flags={columns} hidden={hiddenColumns} onChange={saveColumns} />
            {canCreate && (
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouveau dossier
              </Button>
            )}
          </ListToolbar>
          <ToolbarFilters>
            {shownFilters.includes('frise') && (
              <FilterPill
                mode="single"
                label="Type d’affaire"
                options={frises.map((frise) => ({ value: frise.id, label: friseLabel(frise) }))}
                selected={friseId ? [friseId] : []}
                onChange={(values) => {
                  setFriseId(values[0] ?? null);
                  setStepFilter([]);
                }}
              />
            )}
            {shownFilters.includes('frise') && friseId && (
              <FilterPill
                label="Étape"
                options={stepChoices.map((step) => ({ value: String(step.position), label: step.label }))}
                selected={stepFilter}
                onChange={setStepFilter}
              />
            )}
            {(shownFilters.includes('type') || typeFilter.length > 0) && (
              <FilterPill
                label="Type de dossier"
                options={TYPES.map((t) => ({ value: t, label: DOSSIER_TYPE_LABELS[t] }))}
                selected={typeFilter}
                onChange={(v) => setTypeFilter(v as DossierType[])}
              />
            )}
            {shownFilters.includes('lead') && (
              <PersonSelect
                label="responsables"
                prefix="Responsable"
                emptyLabel="Tous"
                value={leadFilter}
                onChange={setLeadFilter}
              />
            )}
            {(shownFilters.includes('jur') || juridictionFilter.length > 0) && (
              <FilterPill
                label="Juridiction"
                options={juridictionOptions}
                selected={juridictionFilter}
                onChange={setJuridictionFilter}
              />
            )}
            {shownFilters.includes('ech') && (
              <FilterPill
                mode="single"
                label="Échéance"
                options={ECHEANCE_OPTIONS}
                selected={echeanceFilter ? [echeanceFilter] : []}
                onChange={(values) => setEcheanceFilter((values[0] as EcheanceFilter | undefined) ?? null)}
              />
            )}
            {hasActiveFilters && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-[#8a928c] underline underline-offset-2 hover:text-[#1f2823]"
              >
                Réinitialiser
              </button>
            )}
          </ToolbarFilters>
        </div>
        <Card flush className="min-w-0 overflow-hidden" contentClassName="min-w-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : visible.length === 0 ? (
          <ListEmpty
            icon={<FolderOpen />}
            action={
              canCreate && segment !== 'closed' ? (
                <Button variant="ghost" onClick={() => setCreateOpen(true)}>
                  <Plus className="w-4 h-4 mr-1.5" /> Créer un dossier
                </Button>
              ) : null
            }
          >
            Aucun dossier ne correspond.
          </ListEmpty>
        ) : (
          <>
            <div className="w-full min-w-0 max-w-full overflow-x-auto">
              <table className="cime-table" style={{ width: 'max-content', minWidth: '100%' }}>
                <ResizableColumns id="dossiers" />
                <thead className="sticky top-0">
                  <tr>
                    <SortHeader k="affaire" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[280px] max-w-[360px] text-left px-6 py-2.5" />
                    <SortHeader k="client" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[168px] text-left px-3 py-2.5" />
                    {columns.adv && (
                      <SortHeader k="adv" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[168px] text-left px-3 py-2.5" />
                    )}
                    <SortHeader k="equipe" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[108px] text-left px-3 py-2.5" />
                    {columns.matiere && (
                      <SortHeader k="matiere" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[132px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    {columns.juridiction && (
                      <SortHeader k="juridiction" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[132px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    {columns.rg && (
                      <SortHeader k="rg" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[96px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    {columns.aj && (
                      <SortHeader k="aj" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[64px] text-left px-3 py-2.5" />
                    )}
                    <SortHeader k="etape" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[148px] text-left px-3 py-2.5" />
                    {columns.activite && (
                      <SortHeader k="activite" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[168px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    {columns.ouverture && (
                      <SortHeader k="ouverture" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[132px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    {columns.audience && (
                      <SortHeader k="audience" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[132px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    {columns.prescription && (
                      <SortHeader k="prescription" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="min-w-[132px] text-left px-3 py-2.5 whitespace-nowrap" />
                    )}
                    <SortHeader k="echeance" sortKey={sortKey} sortDir={sortDir} onSort={selectSort} className="sticky right-0 z-[1] min-w-[150px] bg-[#fbfaf6] text-left px-3 py-2.5 whitespace-nowrap shadow-[-1px_0_0_#e6e2d8]" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((d) => (
                    <RegistreRow
                      key={d.id}
                      item={d}
                      columns={columns}
                      friseCaption={captionFor(d)}
                      collaboratorName={collaboratorName}
                      collaboratorAvatar={collaboratorAvatar}
                      onClick={() => navigate(`/dossiers/${d.id}`)}
                      onFilterType={(type) => setTypeFilter([type])}
                      onFilterJurisdiction={(name) => setJuridictionFilter([name])}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <ListFooter className="bg-[#fbfaf6] pl-[18px]">
              {visible.length} dossier{visible.length > 1 ? 's' : ''} · {sortCaption(sortKey, sortDir)}
            </ListFooter>
          </>
        )}
        </Card>
      </div>

      {createOpen && <DossierCreateModal onClose={() => setCreateOpen(false)} />}
    </>
  );
}

function SortHeader({
  k, sortKey, sortDir, onSort, className,
}: {
  k: RegistreSortKey;
  sortKey: RegistreSortKey;
  sortDir: SortDir;
  onSort: (key: RegistreSortKey) => void;
  className?: string;
}) {
  const on = sortKey === k;
  return (
    <th
      aria-sort={on ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={className}
    >
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn('inline-flex items-center gap-1 uppercase tracking-inherit', on && 'text-cime-gold')}
      >
        {SORT_LABELS[k]}
        <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" aria-hidden>
          <path d={on && sortDir === 'desc' ? 'M6 10L2 5h8z' : 'M6 2l4 5H2z'} fill="currentColor" />
        </svg>
      </button>
    </th>
  );
}

/** Valeur de cellule cliquable : filtre la liste sur cette valeur, sans ouvrir le dossier. */
function CellFilter({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="rounded text-left underline decoration-transparent underline-offset-2 transition-colors hover:text-cime-primary hover:decoration-cime-primary/50"
    >
      {children}
    </button>
  );
}

function RegistreRow({
  item, columns, friseCaption, collaboratorName, collaboratorAvatar, onClick, onFilterType, onFilterJurisdiction,
}: {
  item: DossierListItem;
  columns: ColumnFlags;
  friseCaption: string | null;
  collaboratorName: (id: string | null | undefined) => string | null;
  collaboratorAvatar: (id: string | null | undefined) => string | null;
  onClick: () => void;
  onFilterType: (type: DossierType) => void;
  onFilterJurisdiction: (name: string) => void;
}) {
  const client = primaryPartyName(item, 'client');
  const opponent = primaryPartyName(item, 'opposing_party');
  const deadline = nextDeadline(item);
  const activity = lastActivity(item);
  const isInactive = !!(item.closed_at || item.archived_at);
  const urgency = deadline ? deadlineUrgency(deadline.date) : null;
  const step = currentStepLabel(item.dossier_frise_steps, dossierBoardRef(item).position)
    ?? DOSSIER_STAGE_LABELS[item.stage];

  const orderedMembers = [...item.dossier_members].sort((a, b) =>
    a.role === 'lead_counsel' ? -1 : b.role === 'lead_counsel' ? 1 : 0,
  );

  return (
    <tr onClick={onClick} className={cn('group cursor-pointer transition', isInactive && 'opacity-55')}>
      <td className="px-6 py-3 max-w-[360px]">
        <span className="block text-[11px] uppercase tracking-wider text-neutral-400">
          {item.number}
          {item.archived_at && <Archive className="inline w-3 h-3 ml-1.5" />}
        </span>
        <span className="block text-[15px] font-semibold text-cime-text-primary leading-snug">
          {item.title}
        </span>
        {item.description && (
          <span className="block text-xs text-neutral-400 truncate">{item.description}</span>
        )}
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        {client
          ? (
            <span className="flex items-center gap-2 text-sm font-semibold text-cime-text-primary">
              <PartyBadge name={client} />
              {client}
            </span>
          )
          : <span className="text-neutral-300">—</span>}
      </td>
      {columns.adv && (
        <td className="px-3 py-3 whitespace-nowrap">
          {opponent
            ? (
              <span className="flex items-center gap-2 text-sm text-cime-text-primary">
                <PartyBadge name={opponent} />
                {opponent}
              </span>
            )
            : <span className="text-neutral-300">—</span>}
        </td>
      )}
      <td className="px-3 py-3">
        <span className="flex items-center">
          {orderedMembers.slice(0, 3).map((m, i) => {
            const name = collaboratorName(m.collaborator_id);
            return (
              <span key={m.collaborator_id} title={name ?? undefined} className={cn(i > 0 && '-ml-1.5')}>
                <Avatar
                  name={name ?? '·'}
                  src={collaboratorAvatar(m.collaborator_id)}
                  size="xs"
                  className={cn(
                    'w-[26px] h-[26px] text-[10px] border-2 border-white',
                    m.role === 'lead_counsel' && 'ring-2 ring-cime-gold',
                  )}
                />
              </span>
            );
          })}
          {orderedMembers.length > 3 && (
            <span className="ml-1 text-[10px] text-neutral-400">+{orderedMembers.length - 3}</span>
          )}
          {orderedMembers.length === 0 && <span className="text-neutral-300">—</span>}
        </span>
      </td>
      {columns.matiere && (
        <td className="px-3 py-3 whitespace-nowrap text-sm text-cime-text-primary">
          {item.type_affaire ? (
            <CellFilter title="Voir tous les dossiers de ce type" onClick={() => onFilterType(item.type_affaire)}>
              {DOSSIER_TYPE_LABELS[item.type_affaire]}
            </CellFilter>
          ) : (
            <span className="text-neutral-300">—</span>
          )}
        </td>
      )}
      {columns.juridiction && (
        <td className="px-3 py-3 whitespace-nowrap text-sm text-cime-text-primary">
          {item.juridiction ? (
            <CellFilter title="Voir tous les dossiers de cette juridiction" onClick={() => onFilterJurisdiction(item.juridiction!)}>
              {item.juridiction}
            </CellFilter>
          ) : (
            <span className="text-neutral-300">—</span>
          )}
        </td>
      )}
      {columns.rg && (
        <td className="px-3 py-3 whitespace-nowrap text-sm text-cime-text-primary">
          {item.rg_number || <span className="text-neutral-300">—</span>}
        </td>
      )}
      {columns.aj && (
        <td className="px-3 py-3">
          {item.aj_flag
            ? <span className="text-[10px] font-bold uppercase tracking-wide text-cime-gold-text">AJ</span>
            : <span className="text-neutral-300">—</span>}
        </td>
      )}
      <td className="px-3 py-3">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border border-neutral-200 bg-neutral-50 text-cime-text-primary">
          {step}
        </span>
        {friseCaption && (
          <span className="block mt-1 text-[11px] text-neutral-400">{friseCaption}</span>
        )}
      </td>
      {columns.activite && (
        <td className="px-3 py-3">
          {activity ? (
            <div className="leading-tight whitespace-nowrap">
              <span className="block text-xs font-medium text-neutral-500">{activity.label}</span>
              <span className="block text-[11px] text-neutral-400">
                {formatDistanceToNow(new Date(activity.at), { addSuffix: true, locale: fr })}
                {collaboratorName(activity.actorId) ? `, ${collaboratorName(activity.actorId)}` : ''}
              </span>
            </div>
          ) : (
            <span className="text-neutral-300">—</span>
          )}
        </td>
      )}
      {columns.ouverture && <DateCell iso={item.opened_at} />}
      {columns.audience && <DateCell iso={item.next_hearing_at} />}
      {columns.prescription && <DateCell iso={item.prescription_date} />}
      <td className="sticky right-0 bg-white px-3 py-3 shadow-[-1px_0_0_#e6e2d8] group-hover:bg-[#fbfaf7]">
        {deadline ? (
          <div className="leading-tight whitespace-nowrap">
            <span className={cn('block text-sm font-semibold', URGENCY_TEXT[urgency ?? 'normal'])}>
              {format(new Date(deadline.date), 'EEE d MMM', { locale: fr })}
            </span>
            <span className="block text-[11px] text-neutral-400 truncate max-w-[180px]">
              {deadline.label}
            </span>
            {(urgency === 'urgent' || urgency === 'overdue') && (
              <span className="inline-block mt-0.5 text-[10px] font-bold uppercase tracking-wide text-[#c05b33] bg-[#c05b33]/10 rounded px-1.5 py-px">
                {urgency === 'overdue'
                  ? 'Dépassée'
                  : `Dans ${Math.max(1, Math.ceil((new Date(deadline.date).getTime() - Date.now()) / 86_400_000))} j`}
              </span>
            )}
          </div>
        ) : isInactive ? (
          <span className="text-neutral-300">—</span>
        ) : (
          <div className="leading-tight">
            <span className="block text-xs text-neutral-400">Aucune échéance —</span>
            <span className="block text-[11px] font-semibold text-[#c05b33]">à planifier</span>
          </div>
        )}
      </td>
    </tr>
  );
}

const PARTY_TONES = ['bg-[#043429]', 'bg-[#8a6d1f]', 'bg-[#a8432a]', 'bg-[#5c6660]', 'bg-[#2b5aa8]', 'bg-[#6b4a8a]'];

/** Pastille aux initiales d'une partie, d'une couleur stable par nom. */
function PartyBadge({ name }: { name: string }) {
  const words = name.trim().split(/\s+/);
  const initials = ((words[0]?.[0] ?? '') + (words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '')).toUpperCase();
  let hash = 0;
  for (const char of name) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return (
    <span
      aria-hidden
      className={cn(
        'grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-[10px] font-bold text-white',
        PARTY_TONES[hash % PARTY_TONES.length],
      )}
    >
      {initials || '?'}
    </span>
  );
}

function DateCell({ iso }: { iso: string | null | undefined }) {
  const label = formatWhen(iso);
  return (
    <td className="px-3 py-3 whitespace-nowrap text-sm text-cime-text-primary">
      {label ?? <span className="text-neutral-300">—</span>}
    </td>
  );
}

function ColumnMenu({
  flags, hidden, onChange,
}: {
  flags: ColumnFlags;
  hidden: OptionalColumn[];
  onChange: (next: ColumnFlags) => void;
}) {
  const [open, setOpen] = useState(false);
  const offered = OPTIONAL_COLUMNS.filter((col) => !hidden.includes(col.key));
  const count = offered.filter((col) => flags[col.key]).length;
  const groups = [
    { id: 'registre', title: 'Dans le registre' },
    { id: 'extra', title: 'À la demande' },
  ] as const;

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex h-8 items-center gap-2 rounded-full border bg-white py-0 pl-3 pr-2.5 text-[13px] font-bold text-[#1f2823]',
          open
            ? 'border-[#043429] shadow-[inset_0_0_0_1px_#043429]'
            : 'border-[#e6e2d8] hover:border-[#cfcab8]',
        )}
      >
        <svg className="text-[#043429]" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
          <rect x="1.5" y="2" width="13" height="12" rx="1.5" />
          <path d="M1.5 6h13M6.5 6v8M11 6v8" />
        </svg>
        Colonnes
        <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#043429] px-[5px] text-[11px] font-bold text-white">{count}</span>
        <MenuChevron open={open} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={cn(MENU_PANEL, 'right-0 w-[280px]')}>
            <p className="mx-2.5 mb-1 mt-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a928c]">Affichage</p>
            {groups.map((group) => (
              <div key={group.id}>
                <p className="mx-2.5 mb-0.5 mt-2 text-[11px] font-bold text-cime-gold-text">{group.title}</p>
                {offered.filter((col) => col.group === group.id).map((col) => {
                  const checked = flags[col.key];
                  return (
                    <label
                      key={col.key}
                      className={cn(
                        'my-px flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-sm text-[#1f2823] hover:bg-[#f4f1e8]',
                        checked && 'bg-[rgba(201,168,76,0.16)] font-bold text-[#043429]',
                      )}
                    >
                      <span>{col.label}</span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => onChange({ ...flags, [col.key]: !checked })}
                      />
                      <MenuMark checked={checked} />
                    </label>
                  );
                })}
              </div>
            ))}
            <p className="mx-2.5 mb-1 mt-2 border-t border-[#e6e2d8] pt-2 text-xs leading-snug text-[#8a928c]">
              Affaire, client, équipe, étape et échéance restent affichés.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
